import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../ui/Card';
import api from '../../services/api';

/**
 * Trainer-facing pre-session analytics tab. Shows aggregated metrics ONLY —
 * self-assessment averages/distributions, challenge/expectation texts without
 * participant attribution, and a completion table with names + counts (no answer
 * texts).
 */
const PreSessionAnalyticsTab = ({ sessionId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [showAllChallenges, setShowAllChallenges] = useState(false);
  const [showAllExpectations, setShowAllExpectations] = useState(false);

  useEffect(() => {
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      // api.js returns parsed JSON directly; read .success / .data (not .data.success).
      const response = await api.get(`/trainer/sessions/${sessionId}/pre-session-analytics`);
      if (response?.success) setAnalytics(response.data);
    } catch (err) {
      console.error('Failed to load pre-session analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-6 text-neutral-600">Loading analytics...</div>;
  if (!analytics) return <div className="p-6 text-neutral-600">No data available</div>;

  return (
    <div className="space-y-6">
      {/* Overview */}
      <Card>
        <div className="p-6">
          <h3 className="text-base font-semibold text-neutral-900 mb-3">Overview</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-neutral-500">Total Participants</p>
              <p className="text-2xl font-bold text-neutral-900">
                {analytics.overview.totalParticipants}
              </p>
            </div>
            <div>
              <p className="text-xs text-neutral-500">Avg Completion</p>
              <p className="text-2xl font-bold text-neutral-900">
                {analytics.overview.averageCompletion}%
              </p>
            </div>
            <div>
              <p className="text-xs text-neutral-500">Session Starts</p>
              <p className="text-sm font-medium text-neutral-900">
                {analytics.overview.sessionStartsAt
                  ? new Date(analytics.overview.sessionStartsAt).toLocaleString()
                  : '—'}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Pre-Read Materials */}
      {analytics.materials.length > 0 && (
        <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-neutral-100 text-center">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
            </div>
            <h4 className="text-base font-bold text-neutral-900">Pre-Read Materials</h4>
            <p className="text-xs text-neutral-500 mt-0.5">How many participants have reviewed each pre-read</p>
          </div>
          <div className="p-4 space-y-2">
            {analytics.materials.map((m, idx) => (
              <div key={idx} className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                <div className="flex items-center justify-between gap-3 text-sm mb-2">
                  <span className="font-medium text-neutral-900 truncate">{m.name}</span>
                  <span className="text-xs font-semibold text-blue-700 flex-shrink-0">
                    {m.viewCount}/{m.totalParticipants} ({m.viewRate}%)
                  </span>
                </div>
                <div className="w-full bg-white rounded-full h-2 overflow-hidden border border-blue-100">
                  <div
                    className="bg-blue-500 h-full rounded-full transition-all"
                    style={{ width: `${m.viewRate}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Self-Assessment */}
      {analytics.selfAssessment.length > 0 && (
        <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-neutral-100 text-center">
            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-2">
              <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
              </svg>
            </div>
            <h4 className="text-base font-bold text-neutral-900">Self-Assessment Results</h4>
            <p className="text-xs text-neutral-500 mt-0.5">How participants rated themselves before the training (aggregated)</p>
          </div>
          <div className="p-4 space-y-3">
            {analytics.selfAssessment.map((s, idx) => {
              const isRating = s.questionType === 'rating';
              const emojiFor = (v) => ['😞', '😑', '🙂', '😄', '🤩'][v - 1] || '';
              return (
                <div key={idx} className="p-3 bg-neutral-50 rounded-xl border border-neutral-100">
                  <p className="text-sm font-semibold text-neutral-800 mb-1">{s.questionText}</p>
                  <p className="text-xs text-neutral-500 mb-3">
                    {s.responseCount} response{s.responseCount === 1 ? '' : 's'}
                    {isRating && s.average !== undefined && (
                      <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full font-semibold">
                        Avg: {s.average}/5
                      </span>
                    )}
                  </p>

                  {isRating && s.distribution && (
                    <div className="space-y-1.5">
                      {s.distribution.map((d, di) => {
                        const total = s.responseCount || 1;
                        const pct = Math.round(((d.count || 0) / total) * 100);
                        return (
                          <div key={di} className="flex items-center gap-2 text-xs">
                            <span className="w-14 flex-shrink-0 flex items-center gap-1">
                              <span className="text-base">{emojiFor(d.star)}</span>
                              <span className="text-neutral-600 font-medium">{d.star}/5</span>
                            </span>
                            <div className="flex-1 h-2 bg-white rounded-full border border-neutral-200 overflow-hidden">
                              <div
                                className="h-full bg-orange-400 rounded-full transition-all"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="w-10 text-right text-neutral-600 flex-shrink-0">{d.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {!isRating && s.distribution && (
                    <div className="flex flex-wrap gap-1.5">
                      {s.distribution.map((d, di) => (
                        <span key={di} className="text-xs bg-white px-2 py-1 rounded-lg border border-neutral-200 text-neutral-700">
                          {d.option}: <span className="font-semibold">{d.count}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Challenges */}
      {analytics.challenges.total > 0 && (
        <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-neutral-100 text-center">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-2">
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <h4 className="text-base font-bold text-neutral-900">Challenges <span className="text-neutral-500 font-normal">({analytics.challenges.total})</span></h4>
            <p className="text-xs text-neutral-500 mt-0.5">Workplace challenges shared before the session — shown unattributed</p>
          </div>
          <div className="p-4">
            <button
              onClick={() => setShowAllChallenges(!showAllChallenges)}
              className="text-sm font-medium text-amber-700 hover:underline mb-3"
            >
              {showAllChallenges ? 'Hide responses' : 'Show all responses'}
            </button>
            {showAllChallenges && (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {analytics.challenges.allResponses.map((c, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
                    <span className="w-6 h-6 rounded-full bg-amber-200 text-amber-700 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                    <p className="text-sm text-neutral-800 leading-relaxed">{c}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Expectations */}
      {analytics.expectations.total > 0 && (
        <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-neutral-100 text-center">
            <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-2">
              <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
              </svg>
            </div>
            <h4 className="text-base font-bold text-neutral-900">Expectations <span className="text-neutral-500 font-normal">({analytics.expectations.total})</span></h4>
            <p className="text-xs text-neutral-500 mt-0.5">What participants hoped to gain from the training — shown unattributed</p>
          </div>
          <div className="p-4">
            <button
              onClick={() => setShowAllExpectations(!showAllExpectations)}
              className="text-sm font-medium text-indigo-700 hover:underline mb-3"
            >
              {showAllExpectations ? 'Hide responses' : 'Show all responses'}
            </button>
            {showAllExpectations && (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {analytics.expectations.allResponses.map((e, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-indigo-50 rounded-xl border border-indigo-100">
                    <span className="w-6 h-6 rounded-full bg-indigo-200 text-indigo-700 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
                    <p className="text-sm text-neutral-800 leading-relaxed">{e}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Completion table */}
      <Card>
        <div className="p-6">
          <h3 className="text-base font-semibold text-neutral-900 mb-3">
            Participant Completion
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-200 text-left">
                  <th className="p-2 font-medium text-neutral-700">Name</th>
                  <th className="p-2 font-medium text-neutral-700">Materials</th>
                  <th className="p-2 font-medium text-neutral-700">Assessment</th>
                  <th className="p-2 font-medium text-neutral-700">Challenges</th>
                  <th className="p-2 font-medium text-neutral-700">Expectations</th>
                  <th className="p-2 font-medium text-neutral-700">Complete</th>
                  <th className="p-2 font-medium text-neutral-700 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {analytics.completionTable.map(p => (
                  <tr key={p.participantId} className="border-b border-neutral-100">
                    <td className="p-2">{p.name}</td>
                    <td className="p-2">{p.materialsViewed}/{p.totalMaterials}</td>
                    <td className="p-2">{p.selfAssessmentDone ? '✅' : '⬜'}</td>
                    <td className="p-2">{p.challengeCount}</td>
                    <td className="p-2">{p.expectationCount}</td>
                    <td className="p-2 font-semibold">{p.completionPercent}%</td>
                    <td className="p-2 text-right">
                      <button
                        onClick={() => navigate(`/trainer/sessions/${sessionId}/participants/${p.participantId}?tab=preSession&only=preSession`)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 border border-primary-200 rounded-lg transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Show details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PreSessionAnalyticsTab;
